import { Checkbox, IconButton, Tbody, Tr, Td, Text, HStack, Tooltip, Icon } from '@chakra-ui/react';
import { Info } from 'lucide-react';
import type { BillableRow } from './types';
import { truncateDescription } from './utils';

interface BillableItemsTableBodyProps {
  rows: BillableRow[];
  canManageXeroInvoices?: boolean;
  selectedIdsVisible: Set<string>;
  toggleSelect: (id: string) => void;
  getProviderName: (providerId: string) => string | null;
  openDetails: (id: string) => void;
}

function DescriptionCell({ description }: { description?: string | null }) {
  const { short, truncated } = truncateDescription(description);

  if (description == null || description.trim() === '' || !short) {
    return <Text color="gray.500">—</Text>;
  }

  return (
    <HStack spacing={1} align="center" maxW="full">
      <Tooltip label={description} isDisabled={!truncated} hasArrow openDelay={300} placement="top">
        <Text isTruncated flex="1 1 auto">
          {short}
        </Text>
      </Tooltip>
      {truncated ? (
        <Icon as={Info} boxSize="3.5" color="gray.400" aria-label="Full text on hover" />
      ) : null}
    </HStack>
  );
}

export function BillableItemsTableBody({
  rows,
  canManageXeroInvoices,
  selectedIdsVisible,
  toggleSelect,
  getProviderName,
  openDetails,
}: BillableItemsTableBodyProps) {
  return (
    <Tbody>
      {rows.map((row) => (
        <Tr key={row.id}>
          {canManageXeroInvoices === true && (
            <Td width="48px">
              <Checkbox
                isChecked={selectedIdsVisible.has(row.id)}
                onChange={() => toggleSelect(row.id)}
                aria-label={`Select ${row.id}`}
              />
            </Td>
          )}
          <Td width="160px" whiteSpace="nowrap">
            {row.providerDrn != null ? getProviderName(row.providerDrn) : '—'}
          </Td>
          <Td width="140px" whiteSpace="nowrap">
            {row.invoiceDate != null && row.invoiceDate !== ''
              ? new Date(row.invoiceDate).toISOString().split('T')[0]
              : '-'}
          </Td>
          <Td width="140px" whiteSpace="nowrap">
            {row.dueDate != null && row.dueDate !== ''
              ? new Date(row.dueDate).toISOString().split('T')[0]
              : '-'}
          </Td>
          <Td width="140px" whiteSpace="nowrap">
            {row.status}
          </Td>
          <Td width="260px">
            <DescriptionCell description={row.description} />
          </Td>
          <Td width="140px" whiteSpace="nowrap">
            {row.unitAmount != null && row.unitAmount !== '' ? row.unitAmount : '—'}
          </Td>
          <Td width="100px" whiteSpace="nowrap">
            {row.currency != null && row.currency !== '' ? row.currency : '—'}
          </Td>
          <Td width="160px" whiteSpace="nowrap">
            {row.xeroInvoiceNumber != null && row.xeroInvoiceNumber !== ''
              ? row.xeroInvoiceNumber
              : '—'}
          </Td>
          <Td width="80px">
            <IconButton
              aria-label="View details"
              icon={<Info size={18} />}
              size="sm"
              variant="ghost"
              onClick={() => openDetails(row.id)}
            />
          </Td>
        </Tr>
      ))}
    </Tbody>
  );
}
